/**
 * MIS Widget for AdminHome — pulls Books dashboard + AR aging via the bridge.
 * Renders nothing if the current user hasn't linked their Books tenant.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Wallet, AlertCircle, Link2, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('emerald_token')}` });

const fmt = (n, cur) => {
  const sym = { GBP: '£', INR: '₹', MYR: 'RM ', KZT: '₸', USD: '$', EUR: '€' }[cur] || '';
  return `${sym}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const BooksMisWidget = () => {
  const [state, setState] = useState({ loading: true, linked: false, mis: null });

  useEffect(() => {
    (async () => {
      try {
        const linkRes = await fetch(`${API}/portal-bridge/books/link`, { headers: authHeaders() });
        const link = await linkRes.json();
        if (!link.linked) { setState({ loading: false, linked: false, mis: null }); return; }
        const misRes = await fetch(`${API}/portal-bridge/books/mis`, { headers: authHeaders() });
        if (!misRes.ok) { setState({ loading: false, linked: true, mis: null, error: true }); return; }
        const mis = await misRes.json();
        setState({ loading: false, linked: true, mis });
      } catch (e) { setState({ loading: false, linked: false, mis: null }); }
    })();
  }, []);

  if (state.loading) return null;

  if (!state.linked) {
    return (
      <div className="bg-gradient-to-br from-[#0b1a2e] to-[#0f5c3d] text-white rounded-2xl p-5 mb-6" data-testid="books-mis-empty">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-white/70">Emerald Books</div>
            <div className="mt-1 text-lg font-medium">Link your accounting tenant to see live MIS here.</div>
          </div>
          <Link to="/admin/books-link"
            className="inline-flex items-center gap-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm font-semibold border border-white/20">
            <Link2 className="w-3.5 h-3.5" /> Link Books
          </Link>
        </div>
      </div>
    );
  }

  if (state.error || !state.mis) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-center gap-3 text-amber-900" data-testid="books-mis-error">
        <AlertCircle className="w-4 h-4" />
        <div className="text-sm">Books token expired — please <Link to="/admin/books-link" className="font-semibold underline">re-link</Link>.</div>
      </div>
    );
  }

  const { dashboard, ar_aging_totals, tenant } = state.mis;
  const cur = tenant?.base_currency;
  const overdueTotal = (ar_aging_totals?.['1_30'] || 0) + (ar_aging_totals?.['31_60'] || 0)
                     + (ar_aging_totals?.['61_90'] || 0) + (ar_aging_totals?.['90_plus'] || 0);

  return (
    <div className="mb-6" data-testid="books-mis-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Emerald Books · {tenant?.company_name}</div>
          <div className="text-[11px] text-[#0b1a2e]/50">Live figures from your accounting tenant</div>
        </div>
        <Link to="/books" className="text-xs text-[#0f5c3d] font-semibold hover:underline inline-flex items-center gap-1">
          Open Books <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={Wallet} label="Cash balance" value={fmt(dashboard.cash_balance, cur)} color="text-[#0f5c3d]" />
        <Tile icon={TrendingUp} label="YTD Revenue" value={fmt(dashboard.ytd_revenue, cur)} color="text-[#0b1a2e]" />
        <Tile icon={TrendingUp} label="YTD Profit" value={fmt(dashboard.ytd_profit, cur)}
              color={dashboard.ytd_profit >= 0 ? 'text-emerald-700' : 'text-red-700'} />
        <Tile icon={AlertCircle} label="Overdue AR" value={fmt(overdueTotal, cur)}
              color={overdueTotal > 0 ? 'text-red-700' : 'text-[#0b1a2e]/40'} />
      </div>
    </div>
  );
};

const Tile = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-black/5 rounded-2xl p-4">
    <div className="flex items-center gap-2 text-[#0b1a2e]/60 mb-1">
      <Icon className="w-3.5 h-3.5" />
      <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
    </div>
    <div className={`text-2xl font-mono font-semibold ${color}`}>{value}</div>
  </div>
);

export default BooksMisWidget;
